import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import { productImageUrl } from './AiLookResults';
import SocialShareButtons from './SocialShareButtons';
import TryOnShareCard from './TryOnShareCard';
import { formatProductPriceEur, selectedProductsTotalEur } from './currency';
import { shortDisplayTitle } from './displayText';
import { setPageMeta } from './pageMeta';
import useModalEscape from './useModalEscape';

const TRYON_FEED_LIMIT = 10;
const TRYON_FEED_SCROLL_KEY = 'od_tryon_feed_scroll_y';
const TRYON_FEED_CACHE_KEY = 'od_tryon_feed_cache_v1';
const TRYON_FEED_CACHE_TTL_MS = 5 * 60 * 1000;
const TRYON_FEED_REFRESH_EVENT = 'od:tryon-feed-refresh';

function readTryOnFeedCache() {
  try {
    const cached = JSON.parse(sessionStorage.getItem(TRYON_FEED_CACHE_KEY) || 'null');
    if (!cached?.savedAt || Date.now() - Number(cached.savedAt) > TRYON_FEED_CACHE_TTL_MS) return null;
    if (!Array.isArray(cached.items)) return null;
    return cached;
  } catch {
    return null;
  }
}

function writeTryOnFeedCache(payload) {
  try {
    sessionStorage.setItem(TRYON_FEED_CACHE_KEY, JSON.stringify({ ...payload, savedAt: Date.now() }));
  } catch {
    // Session cache is best-effort only.
  }
}

function normalizeViewerSource(value) {
  if (!value || typeof value !== 'string') return '';
  if (value.startsWith('data:image/')) return value;
  try {
    return new URL(value, window.location.origin).href;
  } catch {
    return '';
  }
}

function publicTryOnShareId(item) {
  return item?.public_share_id || item?.share_id || (typeof item?.id === 'string' ? item.id : '');
}

export default function TryOnFeed() {
  const { formatDate, locale, t } = useI18n();
  const navigate = useNavigate();
  const initialCacheRef = useRef(readTryOnFeedCache());
  const [items, setItems] = useState(() => initialCacheRef.current?.items || []);
  const [offset, setOffset] = useState(() => Number(initialCacheRef.current?.offset || 0));
  const [loading, setLoading] = useState(() => !(initialCacheRef.current?.items?.length));
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(() => Boolean(initialCacheRef.current?.hasMore));
  const [error, setError] = useState('');
  const [viewer, setViewer] = useState({ open: false, slide: 0, zoom: 1, offsetX: 0, offsetY: 0, sources: [] });
  const [shareCardItem, setShareCardItem] = useState(null);
  const feedRef = useRef(null);
  const loadMoreRef = useRef(null);
  const pinchRef = useRef({ distance: 0, zoom: 1 });
  const dragRef = useRef({ active: false, pointerId: null, startX: 0, startY: 0, offsetX: 0, offsetY: 0 });
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
      }),
    [locale],
  );

  useEffect(() => {
    setPageMeta({
      title: `${t('tryonFeed.title')} | Outfit Detective`,
      description: t('tryonFeed.copy'),
      pathname: '/fit-wall',
      robots: 'index,follow',
    });
  }, [t]);

  const loadPage = useCallback(async (nextOffset = 0, append = false, options = {}) => {
    if (append) setLoadingMore(true);
    else if (!options.silent) setLoading(true);

    try {
      const response = await api.getTryOnFeed({ limit: TRYON_FEED_LIMIT, offset: nextOffset });
      const nextItems = response.data || [];
      setItems((current) => (append ? [...current, ...nextItems] : nextItems));
      setOffset(nextOffset + nextItems.length);
      setHasMore(nextItems.length === TRYON_FEED_LIMIT);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (initialCacheRef.current?.items?.length) {
      setLoading(false);
      window.setTimeout(() => {
        const savedY = Number(sessionStorage.getItem(TRYON_FEED_SCROLL_KEY) || 0);
        if (!Number.isFinite(savedY) || savedY <= 0) return;
        feedRef.current?.scrollTo({ top: savedY, behavior: 'auto' });
        window.scrollTo({ top: savedY, behavior: 'auto' });
      }, 80);
      initialCacheRef.current = null;
      loadPage(0, false, { silent: true });
      return;
    }
    loadPage(0, false);
  }, [loadPage]);

  useEffect(() => {
    function refreshFeed() {
      try {
        sessionStorage.removeItem(TRYON_FEED_CACHE_KEY);
      } catch {
        // Session cache is best-effort only.
      }
      loadPage(0, false, { silent: true });
    }

    window.addEventListener(TRYON_FEED_REFRESH_EVENT, refreshFeed);
    return () => window.removeEventListener(TRYON_FEED_REFRESH_EVENT, refreshFeed);
  }, [loadPage]);

  useEffect(() => {
    function saveFeedScroll() {
      const scrollY = feedRef.current?.scrollTop || window.scrollY || 0;
      sessionStorage.setItem(TRYON_FEED_SCROLL_KEY, String(Math.max(0, Math.round(scrollY))));
    }

    const feedNode = feedRef.current;
    feedNode?.addEventListener('scroll', saveFeedScroll, { passive: true });
    window.addEventListener('scroll', saveFeedScroll, { passive: true });
    window.addEventListener('pagehide', saveFeedScroll);
    return () => {
      saveFeedScroll();
      feedNode?.removeEventListener('scroll', saveFeedScroll);
      window.removeEventListener('scroll', saveFeedScroll);
      window.removeEventListener('pagehide', saveFeedScroll);
    };
  }, []);

  useEffect(() => {
    if (loading || loadingMore) return;
    writeTryOnFeedCache({
      items,
      offset,
      hasMore,
    });
  }, [hasMore, items, loading, loadingMore, offset]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !hasMore || loading || loadingMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadPage(offset, true);
      },
      {
        root: feedRef.current,
        rootMargin: '260px 0px',
        threshold: 0.01,
      },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, loadPage, loading, loadingMore, offset]);

  useEffect(() => {
    function handleFeedPaging(event) {
      if (!['PageDown', 'PageUp', 'Home', 'End'].includes(event.key)) return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target?.closest?.('input, textarea, select, button, a, [contenteditable="true"]')) return;

      const feedNode = feedRef.current;
      const scrollableFeed = feedNode && feedNode.scrollHeight > feedNode.clientHeight + 8;
      const target = scrollableFeed ? feedNode : window;
      const viewport = scrollableFeed ? feedNode.clientHeight : window.innerHeight;
      const currentTop = scrollableFeed ? feedNode.scrollTop : window.scrollY;
      const maxTop = scrollableFeed
        ? feedNode.scrollHeight - feedNode.clientHeight
        : document.documentElement.scrollHeight - window.innerHeight;
      const pageStep = Math.max(320, Math.round(viewport * 0.86));
      let nextTop = currentTop;

      if (event.key === 'PageDown') nextTop = Math.min(maxTop, currentTop + pageStep);
      if (event.key === 'PageUp') nextTop = Math.max(0, currentTop - pageStep);
      if (event.key === 'Home') nextTop = 0;
      if (event.key === 'End') nextTop = maxTop;

      event.preventDefault();
      target.scrollTo({ top: nextTop, behavior: 'smooth' });
    }

    window.addEventListener('keydown', handleFeedPaging);
    return () => window.removeEventListener('keydown', handleFeedPaging);
  }, []);

  function shareData(item) {
    const celebrity = item.event?.celebrity_name || t('tryonShare.celebrityFallback');
    const shareId = publicTryOnShareId(item);
    const url = new URL(shareId ? `/t/${shareId}` : '/fit-wall', window.location.origin).href;
    const isShopTryOn = item.try_on_type === 'selected_products';
    const total = formatSelectedTotal(item);
    return {
      url,
      title: isShopTryOn ? t('tryonShare.shopTitle', { celebrity, total }) : t('tryonShare.title', { celebrity }),
      text: isShopTryOn ? t('tryonShare.shopText', { celebrity, total }) : t('tryonShare.text', { celebrity }),
      media: item.image_url,
    };
  }

  function formatPrice(product) {
    return formatProductPriceEur(product, currencyFormatter, t('detail.priceUnavailable'));
  }

  function formatSelectedTotal(item) {
    const total = selectedProductsTotalEur(item?.selected_products || []);
    if (total === null) return t('tryon.totalUnavailable');
    return currencyFormatter.format(total);
  }

  function openViewer(sources, slide = 1) {
    const validSources = sources.map(normalizeViewerSource).filter(Boolean);
    if (validSources.length === 0) return;
    const safeSlide = Math.min(Math.max(Number(slide) || 1, 1), validSources.length) - 1;
    setViewer({
      open: true,
      slide: safeSlide,
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
      sources: validSources,
    });
  }

  function openPublicTryOn(item) {
    const shareId = publicTryOnShareId(item);
    if (shareId) {
      navigate(`/t/${shareId}`);
      return;
    }
    openViewer([item.image_url, item.event?.image_url], 1);
  }

  function closeViewer() {
    setViewer((current) => ({ ...current, open: false, zoom: 1, offsetX: 0, offsetY: 0 }));
    dragRef.current = { active: false, pointerId: null, startX: 0, startY: 0, offsetX: 0, offsetY: 0 };
  }

  useModalEscape(viewer.open, closeViewer);

  function moveViewer(direction) {
    setViewer((current) => ({
      ...current,
      slide: (current.slide + direction + current.sources.length) % current.sources.length,
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
    }));
  }

  function zoomViewer(delta) {
    setViewer((current) => ({
      ...current,
      zoom: Math.min(4, Math.max(1, Number((current.zoom + delta).toFixed(2)))),
      offsetX: current.zoom + delta <= 1 ? 0 : current.offsetX,
      offsetY: current.zoom + delta <= 1 ? 0 : current.offsetY,
    }));
  }

  function setViewerZoom(nextZoom) {
    const zoom = Math.min(4, Math.max(1, Number(nextZoom.toFixed(2))));
    setViewer((current) => ({
      ...current,
      zoom,
      offsetX: zoom <= 1 ? 0 : current.offsetX,
      offsetY: zoom <= 1 ? 0 : current.offsetY,
    }));
  }

  function moveViewerPan(offsetX, offsetY) {
    setViewer((current) => ({
      ...current,
      offsetX: current.zoom <= 1 ? 0 : offsetX,
      offsetY: current.zoom <= 1 ? 0 : offsetY,
    }));
  }

  function touchDistance(touches) {
    const [first, second] = touches;
    return Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
  }

  function handleViewerWheel(event) {
    event.preventDefault();
    const delta = event.deltaY < 0 ? 0.18 : -0.18;
    zoomViewer(delta);
  }

  function handleViewerTouchStart(event) {
    if (event.touches.length !== 2) return;
    dragRef.current.active = false;
    pinchRef.current = {
      distance: touchDistance(event.touches),
      zoom: viewer.zoom,
    };
  }

  function handleViewerTouchMove(event) {
    if (event.touches.length !== 2 || !pinchRef.current.distance) return;
    event.preventDefault();
    const scale = touchDistance(event.touches) / pinchRef.current.distance;
    setViewerZoom(pinchRef.current.zoom * scale);
  }

  function handleViewerTouchEnd(event) {
    if (event.touches.length < 2) {
      pinchRef.current = { distance: 0, zoom: viewer.zoom };
    }
  }

  function handleViewerPointerDown(event) {
    if (viewer.zoom <= 1 || event.button > 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      active: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: viewer.offsetX,
      offsetY: viewer.offsetY,
    };
  }

  function handleViewerPointerMove(event) {
    const drag = dragRef.current;
    if (!drag.active || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    moveViewerPan(
      drag.offsetX + event.clientX - drag.startX,
      drag.offsetY + event.clientY - drag.startY,
    );
  }

  function handleViewerPointerUp(event) {
    if (dragRef.current.pointerId === event.pointerId) {
      dragRef.current.active = false;
      dragRef.current.pointerId = null;
    }
  }

  useEffect(() => {
    if (!viewer.open) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'ArrowLeft' && viewer.sources.length > 1) moveViewer(-1);
      if (event.key === 'ArrowRight' && viewer.sources.length > 1) moveViewer(1);
      if (event.key === '+' || event.key === '=') zoomViewer(0.25);
      if (event.key === '-') zoomViewer(-0.25);
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [viewer.open, viewer.sources.length]);

  return (
    <div className="ct-fit-wall-layout">
      <aside className="ct-fit-wall-side" aria-label="Outfit Detective">
        <div className="ct-rail-brand-card">
          <img src="/brand/outfit-detective-logo-transparent.svg" alt="Outfit Detective" />
        </div>
      </aside>

      <section className="ct-tryon-feed-page" ref={feedRef}>
      {shareCardItem ? (
        <TryOnShareCard
          item={shareCardItem}
          formatPrice={formatPrice}
          formatSelectedTotal={formatSelectedTotal}
          onClose={() => setShareCardItem(null)}
        />
      ) : null}
      {viewer.open ? (
        <div className="ct-photo-viewer" role="presentation" onMouseDown={closeViewer}>
          <div
            className="ct-photo-viewer-stage"
            role="dialog"
            aria-modal="true"
            aria-label={t('tryonGallery.viewerLabel')}
            onMouseDown={(event) => event.stopPropagation()}
            onWheel={handleViewerWheel}
            onTouchStart={handleViewerTouchStart}
            onTouchMove={handleViewerTouchMove}
            onTouchEnd={handleViewerTouchEnd}
            onPointerDown={handleViewerPointerDown}
            onPointerMove={handleViewerPointerMove}
            onPointerUp={handleViewerPointerUp}
            onPointerCancel={handleViewerPointerUp}
          >
            <div className="ct-photo-viewer-topbar">
              <span>{viewer.slide + 1} / {viewer.sources.length}</span>
              <div>
                <button type="button" onClick={() => zoomViewer(-0.25)} aria-label={t('tryonGallery.zoomOut')}>
                  <i className="ph ph-minus"></i>
                </button>
                <button type="button" onClick={() => zoomViewer(0.25)} aria-label={t('tryonGallery.zoomIn')}>
                  <i className="ph ph-plus"></i>
                </button>
                <button type="button" onClick={closeViewer} aria-label={t('aiDialog.close')}>
                  <i className="ph ph-x"></i>
                </button>
              </div>
            </div>
            {viewer.sources.length > 1 ? (
              <button type="button" className="ct-photo-viewer-nav prev" onClick={() => moveViewer(-1)} aria-label={t('common.previous')}>
                <i className="ph ph-arrow-left"></i>
              </button>
            ) : null}
            <img
              src={viewer.sources[viewer.slide]}
              alt=""
              className={viewer.zoom > 1 ? 'is-zoomed' : ''}
              draggable="false"
              style={{ transform: `translate(${viewer.offsetX}px, ${viewer.offsetY}px) scale(${viewer.zoom})` }}
            />
            {viewer.sources.length > 1 ? (
              <button type="button" className="ct-photo-viewer-nav next" onClick={() => moveViewer(1)} aria-label={t('common.next')}>
                <i className="ph ph-arrow-right"></i>
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {error ? <div className="alert alert-danger">{t('feed.apiError', { error })}</div> : null}
      {loading ? <div className="ct-card ct-empty">{t('common.loading')}</div> : null}

      {!loading && items.length === 0 ? (
        <div className="ct-card ct-empty">
          <h2>{t('tryonFeed.emptyTitle')}</h2>
          <p>{t('tryonFeed.emptyCopy')}</p>
          <Link to="/profile?tab=tryons" className="btn btn-dark">{t('tryonFeed.emptyCta')}</Link>
        </div>
      ) : null}

      <div className="ct-tryon-feed-list">
        {items.map((item) => (
          <article className="ct-tryon-feed-card" key={item.id}>
            <div className="ct-tryon-feed-head">
              <span className="ct-fit-wall-avatar">
                <i className="ph ph-sparkle"></i>
              </span>
              <div>
                <strong>{item.sharer?.name || t('tryonFeed.creatorFallback')}</strong>
                <span>{formatDate(item.public_at || item.created_at, { dateStyle: 'medium', timeStyle: 'short' })}</span>
              </div>
            </div>

            <button
              type="button"
              className="ct-tryon-feed-image-button"
              onClick={() => openPublicTryOn(item)}
              aria-label={t('tryonGallery.openGeneratedImage')}
            >
              <img className="ct-tryon-feed-image" src={item.image_url} alt={t('tryon.resultAlt')} />
            </button>

            <div className="ct-tryon-feed-body">
              <div>
                <p className="ct-kicker">{t('tryonFeed.fitCheck')}</p>
                <h2>{t('tryonShare.title', { celebrity: item.event?.celebrity_name || t('tryonShare.celebrityFallback') })}</h2>
              </div>

              {item.event ? (
                <div className="ct-tryon-feed-original">
                  {item.event.image_url ? (
                    <button
                      type="button"
                      onClick={() => openViewer([item.image_url, item.event.image_url], 2)}
                      aria-label={t('tryonGallery.openOriginalImage')}
                    >
                      <img src={item.event.image_url} alt="" />
                    </button>
                  ) : (
                    <span><i className="ph ph-image"></i></span>
                  )}
                  <div>
                    <Link to={`/events/${item.event.id}`} title={item.event.title || t('tryonGallery.untitled')}>
                      {shortDisplayTitle(item.event.title || t('tryonGallery.untitled'))}
                    </Link>
                    <em>{item.event.source_name || t('tryonGallery.originalPost')}</em>
                  </div>
                </div>
              ) : null}

              {item.try_on_type === 'selected_products' && item.selected_products?.length ? (
                <div className="ct-tryon-feed-products">
                  <strong>{t('tryon.selectedProductsTitle', { total: formatSelectedTotal(item) })}</strong>
                  {item.selected_products.slice(0, 4).map((product) => {
                    const content = (
                      <>
                        {product.image_url ? <img src={productImageUrl(product.image_url)} alt="" /> : <i className="ph ph-shopping-bag"></i>}
                        <span>
                          <b>{product.name}</b>
                          <small>{product.store || product.brand || product.item_category}</small>
                        </span>
                        <em>{formatPrice(product)}</em>
                      </>
                    );
                    const href = product.affiliate_url || product.product_url;
                    return href ? (
                      <a href={href} target="_blank" rel="noreferrer" key={product.candidate_id}>
                        {content}
                      </a>
                    ) : (
                      <span key={product.candidate_id}>{content}</span>
                    );
                  })}
                </div>
              ) : null}

              <SocialShareButtons
                {...shareData(item)}
                label={t('tryonFeed.shareThis')}
              />
              <button
                type="button"
                className="ct-share-card-trigger"
                onClick={() => setShareCardItem(item)}
              >
                <i className="ph ph-image-square"></i>
                {t('shareCard.openButton')}
              </button>
            </div>
          </article>
        ))}
      </div>

      {!loading && items.length > 0 && (hasMore || loadingMore) ? (
        <div className={`ct-feed-load-more ${loadingMore ? 'active' : 'pending'}`} ref={loadMoreRef}>
          {loadingMore || hasMore ? (
            <>
              <span className="ct-input-spinner"></span>
              <span>{t('discover.loadingMore')}</span>
            </>
          ) : null}
        </div>
      ) : null}
      </section>

      <aside className="ct-fit-wall-right">
        <div className="ct-fit-wall-how-card">
          <span className="ct-fit-wall-how-icon">
            <i className="ph ph-magic-wand"></i>
          </span>
          <h2>{t('tryonFeed.rightTitle')}</h2>
          <p>{t('tryonFeed.rightCopy')}</p>
          <Link to="/profile?tab=tryons" className="ct-fit-wall-how-cta">
            <i className="ph ph-images-square"></i>
            {t('tryonFeed.rightCta')}
          </Link>
          <em>{t('tryonFeed.rightBadge')}</em>
        </div>
      </aside>
    </div>
  );
}
